import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, Input, OnChanges, SimpleChanges, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent } from '../../../shared/components/primeng';
import { AuthApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';

export interface JoinOrgRequestData {
  userName: string;
  employeeId: string;
  jobTitle: string;
  email: string;
  requestedDate: string;
  status: string;
  action?: string;
  originalData?: any;
}

@Component({
  selector: 'app-join-organisation-request',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    ModalComponent,
  ],
  templateUrl: './join-organisation-request.component.html',
  styleUrl: './join-organisation-request.component.scss',
})
export class JoinOrganisationRequestComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() filters: Record<string, any> = {};
  constructor(
    private authApiService: AuthApiService,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService
  ) {}

  organisationId: string = '';

  @ViewChild('joinOrgActionCell', { static: false }) joinOrgActionCellTemplate!: TemplateRef<any>;
  @ViewChild('joinOrgStatusCell', { static: false }) joinOrgStatusCellTemplate!: TemplateRef<any>;

  // Join Organisation Requests Columns (no sorting)
  joinOrgColumns: TableColumn[] = [
    {
      key: 'userName',
      label: 'User Name',
      sortable: false,
      width: '15%'
    },
    {
      key: 'employeeId',
      label: 'Employee ID',
      sortable: false,
      width: '12%'
    },
    {
      key: 'jobTitle',
      label: 'Job Title',
      sortable: false,
      width: '15%'
    },
    {
      key: 'email',
      label: 'Email',
      sortable: false,
      width: '20%'
    },
    {
      key: 'requestedDate',
      label: 'Requested Date',
      sortable: false,
      width: '15%'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: '13%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '10%',
      align: 'left',
      fixed: 'right'
    }
  ];

  // Join Organisation Requests Data
  joinOrgData: JoinOrgRequestData[] = [];
  allJoinOrgData: JoinOrgRequestData[] = [];

  // Pagination for Join Org
  joinOrgPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100]
  };

  loading = false;
  joinOrgEmptyMessage = 'No join organisation requests found';
  private isInitialLoad = true;
  private previousFilters: Record<string, any> = {};
  private loadInProgress = false;
  openDropdownId: string | null = null;
  showViewModal = false;
  selectedRequestData: any = null;
  private isOpeningModal = false;

  async ngOnInit(): Promise<void> {
    // Store initial filters
    this.previousFilters = this.filters ? JSON.parse(JSON.stringify(this.filters)) : {};
    
    // First, get the organisation ID from user API with retry logic
    await this.loadOrganisationId();
    
    // If organisationId is still not available, wait a bit more and retry once
    if (!this.organisationId) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      await this.loadOrganisationId();
    }
    
    // Then load the requests
    await this.loadJoinOrgRequests();
    
    // Trigger change detection after initial load
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    
    // Mark initial load as complete after a delay to prevent filter change events from triggering API calls
    setTimeout(() => {
      this.isInitialLoad = false;
      this.cdr.markForCheck();
    }, 300);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Skip filter changes during initial load
    if (this.isInitialLoad) {
      return;
    }

    if (changes['filters'] && !changes['filters'].firstChange) {
      // Compare filters to see if they actually changed
      const currentFiltersStr = JSON.stringify(this.filters || {});
      const previousFiltersStr = JSON.stringify(this.previousFilters);
      
      if (currentFiltersStr !== previousFiltersStr) {
        // Filters actually changed, update and reload
        this.previousFilters = this.filters ? JSON.parse(JSON.stringify(this.filters)) : {};
        this.joinOrgPagination = { ...this.joinOrgPagination, currentPage: 1 };
        this.loadJoinOrgRequests();
      }
    }
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    if (this.joinOrgActionCellTemplate) {
      const actionColumn = this.joinOrgColumns.find(col => col.key === 'action');
      if (actionColumn) {
        actionColumn.cellTemplate = this.joinOrgActionCellTemplate;
      }
    }
    if (this.joinOrgStatusCellTemplate) {
      const statusColumn = this.joinOrgColumns.find(col => col.key === 'status');
      if (statusColumn) {
        statusColumn.cellTemplate = this.joinOrgStatusCellTemplate;
      }
    }
  }


  async loadOrganisationId(): Promise<void> {
    const maxRetries = 3;
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        const userData = await this.authApiService.getUserInfo();
        if (userData && userData['result']) {
          const result = userData['result'];
          // Extract organisation ID from user data - use root level organisationId first
          // As per API response structure: organisationId is at root level, not organisation.id
          this.organisationId = result['organisationId'] || 
                               result['organisation_id'] || 
                               result['orgId'] || 
                               result['org_id'] || 
                               '';
          
          // Fallback: try nested organisation object if root level not found
          if (!this.organisationId && result['organisation']) {
            this.organisationId = result['organisation']['id'] || 
                                 result['organisation']['organisationId'] || 
                                 result['organisation']['organisation_id'] || 
                                 '';
          }
          
          // If we got the organisation ID, break out of retry loop
          if (this.organisationId) {
            return;
          }
        }
      } catch (error: any) {
        console.error(`Error loading organisation ID (attempt ${retryCount + 1}/${maxRetries}):`, error);
        
        // If it's a 401 and we haven't exhausted retries, wait and retry
        if (error?.status === 401 && retryCount < maxRetries - 1) {
          retryCount++;
          // Wait progressively longer: 500ms, 1000ms, 2000ms
          await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
          continue;
        }
        
        // If it's not a 401 or we've exhausted retries, set empty and return
        this.organisationId = '';
        return;
      }
      
      // If we didn't get organisationId but no error, retry
      if (!this.organisationId && retryCount < maxRetries - 1) {
        retryCount++;
        await new Promise(resolve => setTimeout(resolve, 500 * retryCount));
      } else {
        break;
      }
    }
  }

  async loadJoinOrgRequests(): Promise<void> {
    // Prevent concurrent API calls
    if (this.loadInProgress) {
      return;
    }

    if (!this.organisationId) {
      console.error('Organisation ID not available');
      this.joinOrgData = [];
      this.allJoinOrgData = [];
      this.joinOrgPagination = { ...this.joinOrgPagination, totalItems: 0 };
      return;
    }

    this.loadInProgress = true;
    this.loading = true;
    try {
      const page = this.joinOrgPagination.currentPage;
      const size = this.joinOrgPagination.pageSize;
      
      // Get dateRange from filters
      const dateRange = this.filters?.['dateRange'] || null;

      // Call the service method with dateRange
      const response = await this.authApiService.getJoinOrganisationRequests(
        this.organisationId,
        page,
        size,
        dateRange
      );

      // Map API response to JoinOrgRequestData format
      let mappedData: JoinOrgRequestData[] = [];
      let totalItems = 0;

      if (response) {
        let dataArray: any[] = [];
        
        // Extract data array based on response structure
        if (response['result'] && Array.isArray(response['result'])) {
          dataArray = response['result'];
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || 0;
        } else if (response['data'] && Array.isArray(response['data'])) {
          dataArray = response['data'];
          totalItems = response['paginationInfo']?.totalCount || response['totalCount'] || response['total'] || 0;
        } else if (Array.isArray(response)) {
          dataArray = response;
          totalItems = dataArray.length;
        }

        // Map data efficiently - using API mock response structure
        mappedData = dataArray.map((item: any) => {
          // Use the correct API field names from mock response
          const dateValue = item.requested_at || item.requestedDate || item.requested_date || item.createdAt || item.created_at || '';
          return {
            userName: item.user_name || item.userName || item.name || item.preferredUsername || '',
            employeeId: item.emp_id || item.employeeId || item.employee_id || item.empId || '',
            jobTitle: item.job_title || item.jobTitle || '',
            email: item.official_email || item.email || '',
            requestedDate: this.formatDate(dateValue),
            status: item.status || item.requestStatus || item.request_status || 'Pending',
            action: 'View',
            originalData: item
          };
        });
      }

      // Set loading to false first so table knows to render
      this.loading = false;
      
      // Update data properties - use direct assignment for better performance
      this.joinOrgData = mappedData;
      this.allJoinOrgData = mappedData;
      this.joinOrgPagination = {
        ...this.joinOrgPagination,
        totalItems: totalItems
      };
      
      // Manually trigger change detection to ensure table updates immediately
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading join org requests:', error);
      this.loading = false;
      this.joinOrgData = [];
      this.allJoinOrgData = [];
      this.joinOrgPagination = {
        ...this.joinOrgPagination,
        totalItems: 0
      };
      // Trigger change detection even on error
      this.cdr.detectChanges();
    } finally {
      this.loadInProgress = false;
      // Final change detection to ensure everything is updated
      this.cdr.detectChanges();
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  }

  onJoinOrgPageChange(page: number): void {
    this.joinOrgPagination = {
      ...this.joinOrgPagination,
      currentPage: page
    };
    this.loadJoinOrgRequests();
  }

  onJoinOrgPageSizeChange(pageSize: number): void {
    this.joinOrgPagination = {
      ...this.joinOrgPagination,
      pageSize: pageSize,
      currentPage: 1
    };
    this.loadJoinOrgRequests();
  }

  toggleActionDropdown(event: Event, row: JoinOrgRequestData): void {
    event.stopPropagation();
    const rowId = this.getRowId(row);
    const wasOpen = this.openDropdownId === rowId;
    this.openDropdownId = wasOpen ? null : rowId;
    
    // Trigger change detection to ensure dropdown renders
    this.cdr.detectChanges();
    
    // Position dropdown using fixed positioning to escape scroll container
    if (!wasOpen && this.openDropdownId === rowId) {
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        if (!button) return;
        
        const dropdown = document.querySelector(`[data-dropdown-id="${rowId}"]`) as HTMLElement;
        if (dropdown) {
          const rect = button.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
          dropdown.style.width = `${Math.max(rect.width, 120)}px`;
          dropdown.style.minWidth = `${Math.max(rect.width, 120)}px`;
          dropdown.style.display = 'block';
          dropdown.style.visibility = 'visible';
          dropdown.style.opacity = '1';
        }
      }, 10);
    }
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  getRowId(row: JoinOrgRequestData): string {
    return row.originalData?.id || JSON.stringify(row);
  }

  isDropdownOpen(row: JoinOrgRequestData): boolean {
    const rowId = this.getRowId(row);
    return this.openDropdownId === rowId;
  }

  isStatusPending(status: string): boolean {
    if (!status) return false;
    const normalizedStatus = status.toLowerCase().trim();
    return normalizedStatus === 'pending';
  }

  isStatusFinal(status: string): boolean {
    if (!status) return false;
    const normalizedStatus = status.toLowerCase().trim();
    return (
      normalizedStatus === 'approved' ||
      normalizedStatus === 'rejected' ||
      normalizedStatus === 'granted' ||
      normalizedStatus === 'withdrawn' ||
      normalizedStatus === 'withdraw'
    );
  }

  isStatusWithdrawn(status: string): boolean {
    if (!status) return false;
    const s = status.toLowerCase().trim();
    return s === 'withdrawn' || s === 'withdraw';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Don't interfere if we're in the process of opening a modal
    if (this.isOpeningModal) {
      return;
    }
    
    // Close dropdown when clicking outside
    // Don't close if clicking on modal or its backdrop
    const target = event.target as HTMLElement;
    if (target.closest('app-modal') || target.closest('.p-dialog') || target.closest('.p-dialog-mask')) {
      return;
    }
    
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    // Close dropdown when scrolling
    if (this.openDropdownId !== null) {
      this.closeActionDropdown();
    }
  }

  async handleJoinOrgAction(action: string, row: JoinOrgRequestData, event?: Event): Promise<void> {
    // Stop event propagation to prevent document click handler from interfering
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // For view action, set flag to prevent document click from interfering
    if (action === 'view') {
      this.isOpeningModal = true;
    }
    
    // Close dropdown first
    this.closeActionDropdown();
    
    // For view action, open modal immediately after closing dropdown
    if (action === 'view') {
      // Use requestAnimationFrame to ensure dropdown closes and DOM updates before opening modal
      requestAnimationFrame(() => {
        this.openViewModal(row);
        // Reset flag after a short delay to allow modal to render
        setTimeout(() => {
          this.isOpeningModal = false;
        }, 100);
      });
    } else if (action === 'approve') {
      this.toasterService.info('Processing approval...');
      await this.updateJoinRequestStatus(row, 'granted', "You've successfully approved the Organisation Request");
    } else if (action === 'reject') {
      this.toasterService.info('Processing rejection...');
      await this.updateJoinRequestStatus(row, 'rejected', "You've successfully rejected the Organisation Request");
    }
  }

  openViewModal(row: JoinOrgRequestData): void {
    this.selectedRequestData = row.originalData || row;
    this.showViewModal = true;
    // Trigger change detection to ensure modal renders
    this.cdr.detectChanges();
  }

  closeViewModal(): void {
    this.showViewModal = false;
    this.selectedRequestData = null;
  }

  getHighestRole(roles: string[] | undefined): string | null {
    if (!roles || roles.length === 0) {
      return null;
    }

    // Role hierarchy (highest to lowest priority)
    const roleHierarchy = [
      'admin',
      'platform-admin',
      'org-admin',
      'provider',
      'consumer',
      'default-roles-tgdex-pilot',
      'offline_access'
    ];

    // Find the highest role based on hierarchy
    for (const role of roleHierarchy) {
      const foundRole = roles.find(r => r.toLowerCase() === role.toLowerCase());
      if (foundRole) {
        return foundRole;
      }
    }

    // If no role matches hierarchy, return the first role
    return roles[0];
  }

  async updateJoinRequestStatus(row: JoinOrgRequestData, status: string, successMessage: string): Promise<void> {
    const requestId = row.originalData?.id;
    if (!requestId) {
      console.error('Request ID not found');
      return;
    }

    if (!this.organisationId) {
      console.error('Organisation ID not available');
      return;
    }

    try {
      // Prepare payload with status
      const payload = {
        status: status
      };

      // Call the API from AuthApiService
      await this.authApiService.updateJoinOrganisationRequestStatus(
        this.organisationId,
        requestId,
        payload
      );

      // API call succeeded (no throw) - show success toaster
      this.toasterService.success(successMessage);

      // Reload the data to reflect the updated status
      await this.loadJoinOrgRequests();
    } catch (error) {
      console.error(`Error updating join request status to ${status}:`, error);
      this.toasterService.error(`Failed to ${status === 'granted' ? 'approve' : 'reject'} the organisation request. Please try again.`);
    }
  }
}
